import React, { useState, useEffect, useMemo, useRef } from 'react';
import { XMarkIcon, CheckIcon, ChevronDownIcon, BookmarkIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import categoryService from '../services/categoryService';
import CategoryMultiSelect from './CategoryMultiSelect';
import userPreferencesService from '../services/userPreferencesService';
import toast from 'react-hot-toast';

/**
 * Account Filter Dropdown Component
 * Compact dropdown with data pills for selected accounts
 */
const AccountFilterDropdown = ({ accounts, selectedAccountIds, onSelectionChange, onSelectAll }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggleAccount = (accountId) => {
    const newSelection = selectedAccountIds.includes(accountId)
      ? selectedAccountIds.filter(id => id !== accountId)
      : [...selectedAccountIds, accountId];
    
    onSelectionChange(newSelection);
  };

  const handleRemovePill = (accountId, e) => {
    e.stopPropagation();
    const newSelection = selectedAccountIds.filter(id => id !== accountId);
    onSelectionChange(newSelection);
  };

  const selectedAccounts = accounts.filter(acc => selectedAccountIds.includes(acc.id));
  const displayText = selectedAccounts.length === 0 
    ? 'All Accounts' 
    : selectedAccounts.length === 1
    ? selectedAccounts[0].displayName
    : `${selectedAccounts.length} accounts`;

  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-xs font-medium text-gray-700 mb-1">Accounts</label>
      <div className="relative">
        {/* Trigger Button */}
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center gap-2 px-3 py-2 bg-white/80 backdrop-blur-sm border border-gray-200 rounded-lg text-xs text-gray-700 hover:bg-gray-50 focus:ring-purple-500 focus:border-purple-500 transition-colors min-h-[2.5rem]"
        >
          <div className="flex-1 flex items-center gap-2 flex-wrap">
            {selectedAccounts.length === 0 ? (
              <span className="text-gray-500">All Accounts</span>
            ) : (
              selectedAccounts.map(account => (
                <span
                  key={account.id}
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium"
                >
                  {account.displayName}
                  <button
                    type="button"
                    onClick={(e) => handleRemovePill(account.id, e)}
                    className="hover:bg-purple-200 rounded-full p-0.5 transition-colors"
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </span>
              ))
            )}
          </div>
          <ChevronDownIcon 
            className={`h-4 w-4 text-gray-500 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} 
          />
        </button>

        {/* Dropdown Menu */}
        {isOpen && (
          <div className="absolute left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-64 overflow-y-auto">
            <div className="p-2">
              {/* Header Actions */}
              <div className="flex items-center justify-between px-2 py-1.5 border-b border-gray-200 mb-1">
                <button
                  type="button"
                  onClick={onSelectAll}
                  className="text-xs text-purple-600 hover:text-purple-700 font-medium"
                >
                  {selectedAccountIds.length === accounts.length ? 'Deselect All' : 'Select All'}
                </button>
                {selectedAccountIds.length > 0 && (
                  <button
                    type="button"
                    onClick={() => onSelectionChange([])}
                    className="text-xs text-gray-500 hover:text-gray-700 font-medium"
                  >
                    Clear
                  </button>
                )}
              </div>

              {/* Account List */}
              <div className="py-1">
                {accounts.map((account) => {
                  const isSelected = selectedAccountIds.includes(account.id);
                  return (
                    <button
                      key={account.id}
                      type="button"
                      onClick={() => handleToggleAccount(account.id)}
                      className="w-full flex items-center gap-2 px-2 py-2 rounded-md text-xs text-left hover:bg-gray-50 transition-colors"
                    >
                      <div className={`flex-shrink-0 w-4 h-4 border-2 rounded flex items-center justify-center ${
                        isSelected 
                          ? 'bg-purple-600 border-purple-600' 
                          : 'border-gray-300'
                      }`}>
                        {isSelected && (
                          <CheckIcon className="h-3 w-3 text-white" />
                        )}
                      </div>
                      <span className={`flex-1 ${isSelected ? 'font-medium text-gray-900' : 'text-gray-700'}`}>
                        {account.displayName}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const FilterPanel = ({ onFilter, transactions = [], initialFilters = {}, user = null }) => {
  const [filters, setFilters] = useState({
    search: '',
    dateFrom: '',
    dateTo: '',
    amountMin: '',
    amountMax: '',
    type: 'all',
    category: '',
    owner: 'all',
    source: 'all',
    accounts: [], // Array of selected account IDs
    merchant: '', // Merchant name search
    shared: 'all', // Shared status filter (all, shared, personal)
    recurring: 'all', // Recurring filter (all, recurring, one-time)
    notes: '', // Notes search
    ...initialFilters // Merge initial filters if provided
  });
  
  const [savedPresets, setSavedPresets] = useState([]);
  const [loadingPresets, setLoadingPresets] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [presetName, setPresetName] = useState('');
  
  // Load saved presets on mount
  useEffect(() => {
    loadSavedPresets();
  }, []);

  // Update filters when initialFilters change (when reopening with existing filters)
  useEffect(() => {
    if (initialFilters && Object.keys(initialFilters).length > 0) {
      setFilters(prev => ({
        ...prev,
        ...initialFilters
      }));
    }
  }, [initialFilters]);

  // Load saved filter presets
  const loadSavedPresets = async () => {
    setLoadingPresets(true);
    try {
      const presets = await userPreferencesService.getTransactionFilterPresets();
      setSavedPresets(presets || []);
    } catch (error) {
      console.error('Error loading filter presets:', error);
    } finally {
      setLoadingPresets(false);
    }
  };

  // Fun, randomized preset name generators
  const funNameGenerators = {
    expense: [
      '💰 Spending Spree',
      '💸 Money Out',
      '📉 Expense Explorer',
      '🛒 Shopping Focus',
      '💳 Card Activity',
      '📊 Expense Analysis',
      '🎯 Spending Tracker'
    ],
    income: [
      '💵 Income Insight',
      '📈 Money In',
      '💰 Earnings View',
      '💼 Income Tracker',
      '📊 Revenue Report',
      '🎯 Income Focus'
    ],
    category: [
      '🏷️ Category Deep Dive',
      '📂 Category Focus',
      '🎯 Category View',
      '📋 Category Filter',
      '🏷️ Tagged Transactions'
    ],
    personal: [
      '👤 Personal View',
      '🔒 My Transactions',
      '👨‍💼 Personal Only',
      '🎯 Just Mine'
    ],
    partner: [
      '👥 Partner View',
      '💑 Partner Transactions',
      '👫 Shared View',
      '🤝 Partner Focus'
    ],
    joint: [
      '💜 Joint Account',
      '👨‍👩‍👧 Joint View',
      '💑 Together',
      '🤝 Combined View'
    ],
    dateRange: [
      '📅 Time Window',
      '🗓️ Date Range',
      '⏰ Time Frame',
      '📆 Period View',
      '🗓️ Date Filter'
    ],
    amountRange: [
      '💵 Amount Range',
      '💰 Value Filter',
      '💸 Price Range',
      '📊 Amount View'
    ],
    merchant: [
      '🏪 Merchant Focus',
      '🏬 Store View',
      '🛍️ Vendor Filter',
      '🏪 Shop View'
    ],
    recurring: [
      '🔄 Recurring Only',
      '⏰ Regular Payments',
      '📅 Recurring View',
      '🔄 Auto-Payments'
    ],
    search: [
      '🔍 Search Results',
      '🔎 Found It!',
      '📝 Search Filter',
      '🔍 Quick Search'
    ]
  };

  // Generate fun, randomized preset name based on active filters
  const generatePresetName = (filterValues) => {
    const nameParts = [];
    const usedGenerators = new Set();
    
    // Randomly select from available generators based on active filters
    if (filterValues.type && filterValues.type !== 'all') {
      const typeKey = filterValues.type;
      if (funNameGenerators[typeKey] && !usedGenerators.has(typeKey)) {
        const options = funNameGenerators[typeKey];
        nameParts.push(options[Math.floor(Math.random() * options.length)]);
        usedGenerators.add(typeKey);
      }
    }
    
    if (filterValues.category && Array.isArray(filterValues.category) && filterValues.category.length > 0) {
      if (!usedGenerators.has('category')) {
        const options = funNameGenerators.category;
        nameParts.push(options[Math.floor(Math.random() * options.length)]);
        usedGenerators.add('category');
      }
    }
    
    if (filterValues.owner && filterValues.owner !== 'all') {
      if (!usedGenerators.has(filterValues.owner)) {
        const options = funNameGenerators[filterValues.owner];
        if (options) {
          nameParts.push(options[Math.floor(Math.random() * options.length)]);
          usedGenerators.add(filterValues.owner);
        }
      }
    }
    
    if (filterValues.dateFrom || filterValues.dateTo) {
      if (!usedGenerators.has('dateRange')) {
        const options = funNameGenerators.dateRange;
        nameParts.push(options[Math.floor(Math.random() * options.length)]);
        usedGenerators.add('dateRange');
      }
    }
    
    if (filterValues.amountMin || filterValues.amountMax) {
      if (!usedGenerators.has('amountRange')) {
        const options = funNameGenerators.amountRange;
        nameParts.push(options[Math.floor(Math.random() * options.length)]);
        usedGenerators.add('amountRange');
      }
    }
    
    if (filterValues.merchant) {
      if (!usedGenerators.has('merchant')) {
        const options = funNameGenerators.merchant;
        nameParts.push(options[Math.floor(Math.random() * options.length)]);
        usedGenerators.add('merchant');
      }
    }
    
    if (filterValues.recurring && filterValues.recurring !== 'all') {
      if (!usedGenerators.has('recurring')) {
        const options = funNameGenerators.recurring;
        nameParts.push(options[Math.floor(Math.random() * options.length)]);
        usedGenerators.add('recurring');
      }
    }
    
    if (filterValues.search) {
      if (!usedGenerators.has('search')) {
        const options = funNameGenerators.search;
        nameParts.push(options[Math.floor(Math.random() * options.length)]);
        usedGenerators.add('search');
      }
    }
    
    // If no specific filters, use a generic fun name
    if (nameParts.length === 0) {
      const genericNames = [
        '🌟 All Transactions',
        '📊 Full View',
        '🔍 Everything',
        '📋 Complete List',
        '🎯 All In One'
      ];
      return genericNames[Math.floor(Math.random() * genericNames.length)];
    }
    
    // Combine up to 2 name parts for variety
    return nameParts.slice(0, 2).join(' • ');
  };

  // Save current filters as a preset
  const handleSavePreset = async () => {
    if (!presetName.trim()) {
      toast.error('Please enter a name for the filter preset');
      return;
    }

    // Check max limit of 10 presets
    if (savedPresets.length >= 10) {
      toast.error('Maximum of 10 saved filters allowed. Please delete one before saving a new preset.');
      return;
    }

    try {
      setLoadingPresets(true);
      
      // Create new preset
      const newPreset = {
        id: `preset-${Date.now()}`,
        name: presetName.trim(),
        filters: { ...filters },
        createdAt: new Date().toISOString()
      };
      
      // Add to existing presets
      const updatedPresets = [...savedPresets, newPreset];
      
      // Save to backend
      await userPreferencesService.saveTransactionFilterPresets(updatedPresets);
      
      setSavedPresets(updatedPresets);
      setShowSaveModal(false);
      setPresetName('');
      toast.success(`Filter preset "${presetName}" saved successfully`);
    } catch (error) {
      console.error('Error saving filter preset:', error);
      toast.error('Failed to save filter preset');
    } finally {
      setLoadingPresets(false);
    }
  };

  // Update an existing preset
  const handleUpdatePreset = async (presetId) => {
    try {
      setLoadingPresets(true);
      
      const updatedPresets = savedPresets.map(preset => 
        preset.id === presetId
          ? { ...preset, filters: { ...filters }, updatedAt: new Date().toISOString() }
          : preset
      );
      
      await userPreferencesService.saveTransactionFilterPresets(updatedPresets);
      
      setSavedPresets(updatedPresets);
      toast.success('Filter preset updated successfully');
    } catch (error) {
      console.error('Error updating filter preset:', error);
      toast.error('Failed to update filter preset');
    } finally {
      setLoadingPresets(false);
    }
  };

  // Load a saved preset
  const handleLoadPreset = (preset) => {
    setFilters(preset.filters);
    onFilter(preset.filters);
    toast.success(`Loaded filter preset: ${preset.name}`);
  };

  // Delete a saved preset
  const handleDeletePreset = async (presetId, e) => {
    e.stopPropagation();
    
    if (!window.confirm('Are you sure you want to delete this filter preset?')) {
      return;
    }

    try {
      setLoadingPresets(true);
      
      const updatedPresets = savedPresets.filter(p => p.id !== presetId);
      await userPreferencesService.saveTransactionFilterPresets(updatedPresets);
      
      setSavedPresets(updatedPresets);
      toast.success('Filter preset deleted');
    } catch (error) {
      console.error('Error deleting filter preset:', error);
      toast.error('Failed to delete filter preset');
    } finally {
      setLoadingPresets(false);
    }
  };
  
  // Extract unique accounts from transactions
  const uniqueAccounts = useMemo(() => {
    const accountMap = new Map();
    
    transactions.forEach(tx => {
      // Get account/wallet identifier from transaction
      let accountId = null;
      let accountName = null;
      let accountDisplay = null;
      
      // Try wallet first
      if (tx.wallet) {
        if (typeof tx.wallet === 'object') {
          accountId = tx.wallet._id?.toString() || tx.wallet.toString();
          accountName = tx.wallet.name || tx.wallet.institution || 'Unknown';
          const lastFour = tx.wallet.lastFourDigits || tx.wallet.mask || '';
          accountDisplay = lastFour ? `${accountName} ••${lastFour}` : accountName;
        } else if (typeof tx.wallet === 'string') {
          accountId = tx.wallet;
          accountDisplay = 'Account ' + accountId.substring(0, 8);
        }
      }
      
      // Fallback to account
      if (!accountId && tx.account) {
        if (typeof tx.account === 'object') {
          accountId = tx.account._id?.toString() || tx.account.toString();
          accountName = tx.account.name || 'Unknown';
          accountDisplay = accountName;
        } else if (typeof tx.account === 'string') {
          accountId = tx.account;
          accountDisplay = 'Account ' + accountId.substring(0, 8);
        }
      }
      
      // Also check metadata for account info
      if (!accountDisplay && tx.metadata) {
        const institution = tx.metadata.institutionName || tx.metadata.original?.institutionName;
        const mask = tx.metadata.accountMask || tx.metadata.mask;
        if (institution) {
          accountDisplay = mask ? `${institution} ••${mask}` : institution;
        }
      }
      
      if (accountId && !accountMap.has(accountId)) {
        accountMap.set(accountId, {
          id: accountId,
          name: accountDisplay || accountName || 'Unknown Account',
          displayName: accountDisplay || accountName || 'Unknown Account'
        });
      }
    });
    
    return Array.from(accountMap.values()).sort((a, b) => 
      a.displayName.localeCompare(b.displayName)
    );
  }, [transactions]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const response = await categoryService.getAllCategories();
        if (response.success) {
          setCategories(response.data);
        } else {
          console.error('Failed to fetch categories:', response.message);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchCategories();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle category multi-select change
  const handleCategoryChange = (selectedCategoryIds) => {
    setFilters(prev => ({
      ...prev,
      category: selectedCategoryIds
    }));
  };
  
  const handleAccountToggle = (accountId) => {
    setFilters(prev => {
      const currentAccounts = prev.accounts || [];
      const newAccounts = currentAccounts.includes(accountId)
        ? currentAccounts.filter(id => id !== accountId)
        : [...currentAccounts, accountId];
      
      return {
        ...prev,
        accounts: newAccounts
      };
    });
  };
  
  const handleSelectAllAccounts = () => {
    const allAccountIds = uniqueAccounts.map(acc => acc.id);
    setFilters(prev => ({
      ...prev,
      accounts: prev.accounts?.length === allAccountIds.length ? [] : allAccountIds
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onFilter(filters);
  };

  const handleReset = () => {
    setFilters({
      search: '',
      dateFrom: '',
      dateTo: '',
      amountMin: '',
      amountMax: '',
      type: 'all',
      category: [], // Reset to empty array for multi-select
      owner: 'all',
      source: 'all',
      accounts: [],
      merchant: '',
      shared: 'all',
      recurring: 'all',
      notes: ''
    });
    onFilter({});
  };

  // Check if there are any active filters
  const hasActiveFilters = useMemo(() => {
    return !(
      (!filters.search || filters.search.trim() === '') &&
      (!filters.dateFrom || filters.dateFrom === '') &&
      (!filters.dateTo || filters.dateTo === '') &&
      (!filters.amountMin || filters.amountMin === '') &&
      (!filters.amountMax || filters.amountMax === '') &&
      (!filters.type || filters.type === 'all') &&
      (!filters.category || (Array.isArray(filters.category) && filters.category.length === 0) || filters.category === '') &&
      (!filters.owner || filters.owner === 'all') &&
      (!filters.source || filters.source === 'all') &&
      (!filters.accounts || filters.accounts.length === 0) &&
      (!filters.merchant || filters.merchant.trim() === '') &&
      (!filters.shared || filters.shared === 'all') &&
      (!filters.recurring || filters.recurring === 'all') &&
      (!filters.notes || filters.notes.trim() === '')
    );
  }, [filters]);

  return (
    <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 shadow-sm">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-md font-semibold text-gray-800">Filter Transactions</h3>
        <button onClick={() => onFilter({})} className="text-gray-500 hover:text-gray-800">
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      {/* Saved Presets Section */}
      <div className="mb-4 pb-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-2">
          <label className="block text-xs font-medium text-gray-700">
            Saved Filters {savedPresets.length > 0 && `(${savedPresets.length}/10)`}
          </label>
          {hasActiveFilters && (
            <button
              onClick={() => {
                const autoName = generatePresetName(filters);
                setPresetName(autoName);
                setShowSaveModal(true);
              }}
              disabled={savedPresets.length >= 10}
              className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title={savedPresets.length >= 10 ? 'Maximum 10 saved filters. Delete one to save more.' : 'Save current filters'}
            >
              <PlusIcon className="h-3 w-3" />
              Save Current
            </button>
          )}
        </div>
        
        {loadingPresets ? (
          <div className="text-xs text-gray-500">Loading presets...</div>
        ) : savedPresets.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {savedPresets.map((preset) => {
              const isCurrentPreset = JSON.stringify(preset.filters) === JSON.stringify(filters);
              return (
                <div
                  key={preset.id}
                  className={`group relative flex items-center gap-1 px-2 py-1 border rounded text-xs cursor-pointer transition-colors ${
                    isCurrentPreset
                      ? 'bg-purple-100 border-purple-300 text-purple-800'
                      : 'bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100'
                  }`}
                  onClick={() => handleLoadPreset(preset)}
                  title={`Load: ${preset.name}${isCurrentPreset ? ' (Current)' : ''}`}
                >
                  <BookmarkIcon className="h-3 w-3" />
                  <span className="max-w-[150px] truncate">{preset.name}</span>
                  <div className="flex items-center gap-1 ml-1">
                    {isCurrentPreset && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUpdatePreset(preset.id);
                        }}
                        className="opacity-0 group-hover:opacity-100 text-blue-600 hover:text-blue-700 transition-opacity"
                        title="Update preset with current filters"
                      >
                        <CheckIcon className="h-3 w-3" />
                      </button>
                    )}
                    <button
                      onClick={(e) => handleDeletePreset(preset.id, e)}
                      className="opacity-0 group-hover:opacity-100 text-red-600 hover:text-red-700 transition-opacity"
                      title="Delete preset"
                    >
                      <TrashIcon className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-xs text-gray-500">
            No saved filters. {hasActiveFilters && 'Click "Save Current" to save your filters.'}
          </div>
        )}
      </div>

      {/* Save Preset Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={() => {
          setShowSaveModal(false);
          setPresetName('');
        }}>
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Save Filter Preset</h3>
            {savedPresets.length >= 10 && (
              <p className="text-sm text-red-600 mb-4">
                ⚠️ Maximum of 10 saved filters reached. Delete one to save more.
              </p>
            )}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Preset Name
              </label>
              <input
                type="text"
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                placeholder="Enter preset name"
                autoFocus
                maxLength={50}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (savedPresets.length < 10) {
                      handleSavePreset();
                    }
                  } else if (e.key === 'Escape') {
                    setShowSaveModal(false);
                    setPresetName('');
                  }
                }}
              />
              <p className="text-xs text-gray-500 mt-1">
                💡 Tip: The name is auto-generated based on your filters, but you can customize it!
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowSaveModal(false);
                  setPresetName('');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSavePreset}
                disabled={loadingPresets || !presetName.trim() || savedPresets.length >= 10}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loadingPresets ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
            <select
              name="type"
              value={filters.type}
              onChange={handleChange}
              className="w-full px-3 py-2 bg-white/80 backdrop-blur-sm border border-gray-200 rounded-lg text-xs text-gray-700 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="all">All Types</option>
              <option value="expense">Expenses</option>
              <option value="income">Income</option>
            </select>
          </div>
          
          <CategoryMultiSelect
            categories={categories}
            selectedCategoryIds={Array.isArray(filters.category) ? filters.category : (filters.category ? [filters.category] : [])}
            onSelectionChange={handleCategoryChange}
          />

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Owner</label>
            <select
              name="owner"
              value={filters.owner}
              onChange={handleChange}
              className="w-full px-3 py-2 bg-white/80 backdrop-blur-sm border border-gray-200 rounded-lg text-xs text-gray-700 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="all">All Owners</option>
              <option value="personal">Personal</option>
              <option value="partner">Partner</option>
              <option value="joint">Joint</option>
            </select>
          </div>
        </div>
        
        {/* Account Filter - Dropdown with pills */}
        {uniqueAccounts.length > 0 && (
          <AccountFilterDropdown
            accounts={uniqueAccounts}
            selectedAccountIds={filters.accounts || []}
            onSelectionChange={(newSelection) => {
              setFilters(prev => ({ ...prev, accounts: newSelection }));
            }}
            onSelectAll={handleSelectAllAccounts}
          />
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Source</label>
            <select
              name="source"
              value={filters.source}
              onChange={handleChange}
              className="w-full px-3 py-2 bg-white/80 backdrop-blur-sm border border-gray-200 rounded-lg text-xs text-gray-700 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="all">All Sources</option>
              <option value="manual">Manual Only</option>
              <option value="plaid">Plaid Only</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Shared</label>
            <select
              name="shared"
              value={filters.shared}
              onChange={handleChange}
              className="w-full px-3 py-2 bg-white/80 backdrop-blur-sm border border-gray-200 rounded-lg text-xs text-gray-700 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="all">All</option>
              <option value="personal">Personal (Me)</option>
              {(user?.partner?.firstName || user?.createdPartner?.firstName) && (
                <option value="partner">
                  {user?.partner?.firstName || user?.createdPartner?.firstName || 'Partner'}
                </option>
              )}
              <option value="joint">Joint</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Recurring</label>
            <select
              name="recurring"
              value={filters.recurring}
              onChange={handleChange}
              className="w-full px-3 py-2 bg-white/80 backdrop-blur-sm border border-gray-200 rounded-lg text-xs text-gray-700 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="all">All</option>
              <option value="recurring">Recurring Only</option>
              <option value="one-time">One-Time Only</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Search Description</label>
            <input
              type="text"
              name="search"
              value={filters.search}
              onChange={handleChange}
              placeholder="Search transactions..."
              className="w-full px-3 py-2 bg-white/80 backdrop-blur-sm border border-gray-200 rounded-lg text-xs text-gray-700 placeholder-gray-400 focus:ring-purple-500 focus:border-purple-500"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Merchant</label>
            <input
              type="text"
              name="merchant"
              value={filters.merchant}
              onChange={handleChange}
              placeholder="Search by merchant name..."
              className="w-full px-3 py-2 bg-white/80 backdrop-blur-sm border border-gray-200 rounded-lg text-xs text-gray-700 placeholder-gray-400 focus:ring-purple-500 focus:border-purple-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
            <input
              type="text"
              name="notes"
              value={filters.notes}
              onChange={handleChange}
              placeholder="Search in notes..."
              className="w-full px-3 py-2 bg-white/80 backdrop-blur-sm border border-gray-200 rounded-lg text-xs text-gray-700 placeholder-gray-400 focus:ring-purple-500 focus:border-purple-500"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Date Range</label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <input
                  type="date"
                  name="dateFrom"
                  value={filters.dateFrom}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-white/80 backdrop-blur-sm border border-gray-200 rounded-lg text-xs text-gray-700 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="From"
                />
              </div>
              <div>
                <input
                  type="date"
                  name="dateTo"
                  value={filters.dateTo}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-white/80 backdrop-blur-sm border border-gray-200 rounded-lg text-xs text-gray-700 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="To"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Amount Range</label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <input
                  type="number"
                  name="amountMin"
                  value={filters.amountMin}
                  onChange={handleChange}
                  placeholder="Min Amount"
                  className="w-full px-3 py-2 bg-white/80 backdrop-blur-sm border border-gray-200 rounded-lg text-xs text-gray-700 placeholder-gray-400 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
              <div>
                <input
                  type="number"
                  name="amountMax"
                  value={filters.amountMax}
                  onChange={handleChange}
                  placeholder="Max Amount"
                  className="w-full px-3 py-2 bg-white/80 backdrop-blur-sm border border-gray-200 rounded-lg text-xs text-gray-700 placeholder-gray-400 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-2">
          <button
            type="button"
            onClick={handleReset}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
          >
            Reset
          </button>
          <button
            type="submit"
            className="px-4 py-2 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
          >
            Apply Filters
          </button>
        </div>
      </form>
    </div>
  );
};

export default FilterPanel; 